import React, { useState, useEffect } from 'react';
import { 
    Modal, 
    Text, 
    Button, 
    Group, 
    Select, 
    Stack, 
    Divider, 
    Radio, 
    Paper,
    Title,
    Box,
    Alert
} from '@mantine/core';
import { IconPlus, IconAlertTriangle } from '@tabler/icons-react';
import { PersonBetter, Relationship, LifeEvent, RelationshipType } from '../../types/Person';
import { RelationshipList } from './shared/RelationshipList';
import { LifeEventList } from './shared/LifeEventList';
import { NotesEditor } from './shared/NotesEditor';
import { PersonalInfoEditor } from './shared/PersonalInfoEditor';

interface MergePersonModalProps {
    opened: boolean;
    onClose: () => void;
    sourcePerson: PersonBetter;
    targetPersons: PersonBetter[];
    onMerge: (sourcePerson: PersonBetter, targetPerson: PersonBetter, mergeOptions: MergeOptions) => void;
}

interface MergeOptions {
    firstName: string;
    middleName: string | undefined;
    lastName: string;
    dob: Date | undefined;
    profileImage: string | undefined;
    notes: string | null | undefined;
    relationships: Relationship[];
    lifeEvents: LifeEvent[];
}

interface RelationshipWithSelected extends Relationship {
    selected: boolean;
    source: 'source' | 'target';
}

interface LifeEventWithSelected extends LifeEvent {
    selected: boolean;
    source: 'source' | 'target';
}

export function MergePersonModalNew({ 
    opened, 
    onClose, 
    sourcePerson, 
    targetPersons,
    onMerge 
}: MergePersonModalProps) {
    // State for the two-page workflow
    const [activeStep, setActiveStep] = useState(0);
    const [selectedTargetPerson, setSelectedTargetPerson] = useState<PersonBetter | null>(null);
    
    // State for merge options
    const [mergeOptions, setMergeOptions] = useState<MergeOptions>({
        firstName: '',
        middleName: undefined,
        lastName: '',
        dob: undefined,
        profileImage: undefined,
        notes: undefined,
        relationships: [],
        lifeEvents: []
    });
    
    // State for combined relationships and life events
    const [combinedRelationships, setCombinedRelationships] = useState<RelationshipWithSelected[]>([]);
    const [combinedLifeEvents, setCombinedLifeEvents] = useState<LifeEventWithSelected[]>([]);
    
    // State for conflict detection
    const [hasNameConflict, setHasNameConflict] = useState(false);
    const [hasMiddleNameConflict, setHasMiddleNameConflict] = useState(false);
    const [hasLastNameConflict, setHasLastNameConflict] = useState(false);
    const [hasDobConflict, setHasDobConflict] = useState(false);
    const [hasProfileImageConflict, setHasProfileImageConflict] = useState(false);

    // Reset state when modal opens
    useEffect(() => {
        if (opened && sourcePerson) {
            setActiveStep(0);
            setSelectedTargetPerson(null);
            setMergeOptions({
                firstName: sourcePerson.first,
                middleName: sourcePerson.middle,
                lastName: sourcePerson.last,
                dob: sourcePerson.dob,
                profileImage: sourcePerson.profile_url,
                notes: sourcePerson.notes,
                relationships: [],
                lifeEvents: []
            });
            setCombinedRelationships([]);
            setCombinedLifeEvents([]);
        }
    }, [opened, sourcePerson]);

    // Check for conflicts when target person is selected
    useEffect(() => {
        if (!selectedTargetPerson) return;
        
        // Check for conflicts
        setHasNameConflict(sourcePerson.first !== selectedTargetPerson.first);
        setHasMiddleNameConflict(sourcePerson.middle !== selectedTargetPerson.middle);
        setHasLastNameConflict(sourcePerson.last !== selectedTargetPerson.last);
        const dobsAreDifferent: boolean = Boolean(
            (sourcePerson.dob && selectedTargetPerson.dob && 
             sourcePerson.dob.toISOString() !== selectedTargetPerson.dob.toISOString()) ||
            (sourcePerson.dob && !selectedTargetPerson.dob) || 
            (!sourcePerson.dob && selectedTargetPerson.dob)
        );
            
        setHasDobConflict(dobsAreDifferent);
        setHasProfileImageConflict(sourcePerson.profile_url !== selectedTargetPerson.profile_url);
        
        // Combine notes from both people
        const combinedNotes = [];
        if (sourcePerson.notes) combinedNotes.push(`Source notes: ${sourcePerson.notes}`);
        if (selectedTargetPerson.notes) combinedNotes.push(`Target notes: ${selectedTargetPerson.notes}`);
        const mergedNotes = combinedNotes.length > 0 ? combinedNotes.join('\n\n') : null;
        
        // Update merge options with combined notes
        setMergeOptions(prev => ({
            ...prev,
            notes: mergedNotes
        }));
        
        // Process relationships
        const sourceRelationships = (sourcePerson.relationships || []).map(rel => ({
            ...rel,
            selected: true,
            source: 'source' as 'source' | 'target'
        }));
        
        const targetRelationships = (selectedTargetPerson.relationships || []).map(rel => ({
            ...rel,
            selected: true,
            source: 'target' as 'source' | 'target'
        }));
        
        // Combine relationships, marking duplicates
        const combined = [...sourceRelationships];
        
        targetRelationships.forEach(targetRel => {
            // Check if this relationship already exists in source
            const isDuplicate = sourceRelationships.some(sourceRel => 
                sourceRel.name === targetRel.name && 
                sourceRel.type === targetRel.type
            );
            
            if (!isDuplicate) {
                combined.push(targetRel);
            }
        });
        
        setCombinedRelationships(combined);
        
        // Process life events
        const sourceEvents = (sourcePerson.life_events || []).map(event => ({
            ...event,
            selected: true,
            source: 'source' as 'source' | 'target'
        }));
        
        const targetEvents = (selectedTargetPerson.life_events || []).map(event => ({
            ...event,
            selected: true,
            source: 'target' as 'source' | 'target'
        }));
        
        // Combine life events, marking duplicates
        const combinedEvents = [...sourceEvents];
        
        targetEvents.forEach(targetEvent => {
            // Check if this event already exists in source (same type and date)
            const isDuplicate = sourceEvents.some(sourceEvent => 
                sourceEvent.type === targetEvent.type && 
                sourceEvent.date?.toString() === targetEvent.date?.toString()
            );
            
            if (!isDuplicate) {
                combinedEvents.push(targetEvent);
            }
        });
        
        setCombinedLifeEvents(combinedEvents);
        
        // Update merge options with combined data
        updateMergeOptionsFromSelections(combined, combinedEvents);
        
    }, [selectedTargetPerson, sourcePerson]);

    const updateMergeOptionsFromSelections = (relationships: RelationshipWithSelected[], lifeEvents: LifeEventWithSelected[]) => {
        setMergeOptions(prev => ({
            ...prev,
            relationships: relationships.filter(r => r.selected).map(r => ({
                type: r.type,
                name: r.name,
                person: r.person,
                custom_type: r.custom_type
            })),
            lifeEvents: lifeEvents.filter(e => e.selected).map(e => ({
                type: e.type,
                date: e.date,
                description: e.description,
                isLocked: e.isLocked
            }))
        }));
    };
    
    const handleAddRelationship = () => {
        setCombinedRelationships(prev => [
            ...prev,
            {
                type: null,
                name: null,
                selected: true,
                source: 'source'
            }
        ]);
    };
    
    const handleAddLifeEvent = () => {
        setCombinedLifeEvents(prev => [
            ...prev,
            {
                type: '',
                date: null,
                description: '',
                selected: true,
                source: 'source'
            }
        ]);
    };

    const handleTargetPersonChange = (targetPersonId: string | null) => {
        if (!targetPersonId) {
            setSelectedTargetPerson(null);
            return;
        }
        
        const targetPerson = targetPersons.find(person => person.seed_key === targetPersonId);
        if (targetPerson) {
            setSelectedTargetPerson(targetPerson);
        }
    };

    const handleMergeOptionChange = (field: keyof MergeOptions, value: any) => {
        setMergeOptions(prev => ({
            ...prev,
            [field]: value
        }));
    };
    
    const handleUpdateRelationship = (index: number, field: keyof Relationship, value: any) => {
        setCombinedRelationships(prev => {
            const updated = [...prev];
            updated[index] = {
                ...updated[index],
                [field]: value
            };
            updateMergeOptionsFromSelections(updated, combinedLifeEvents);
            return updated;
        });
    };
    
    const handleRemoveRelationship = (index: number) => {
        setCombinedRelationships(prev => {
            const updated = prev.map((rel, i) => 
                i === index ? { ...rel, selected: false } : rel
            );
            updateMergeOptionsFromSelections(updated, combinedLifeEvents);
            return updated;
        });
    };
    
    const handleUpdateLifeEvent = (index: number, field: keyof LifeEvent, value: any) => {
        setCombinedLifeEvents(prev => {
            const updated = [...prev];
            updated[index] = {
                ...updated[index],
                [field]: value
            };
            updateMergeOptionsFromSelections(combinedRelationships, updated);
            return updated;
        });
    };
    
    const handleRemoveLifeEvent = (index: number) => {
        setCombinedLifeEvents(prev => {
            const updated = prev.map((event, i) => 
                i === index ? { ...event, selected: false } : event
            );
            updateMergeOptionsFromSelections(combinedRelationships, updated);
            return updated;
        });
    };

    const handleContinue = () => {
        setActiveStep(1);
    };

    const handleMerge = () => {
        if (selectedTargetPerson) {
            // Update final merge options with current selections
            const finalOptions = {
                ...mergeOptions,
                relationships: combinedRelationships.filter(r => r.selected).map(r => ({
                    type: r.type,
                    name: r.name,
                    person: r.person,
                    custom_type: r.custom_type
                })),
                lifeEvents: combinedLifeEvents.filter(e => e.selected).map(e => ({
                    type: e.type,
                    date: e.date,
                    description: e.description,
                    isLocked: e.isLocked
                }))
            };
            
            onMerge(sourcePerson, selectedTargetPerson, finalOptions);
            onClose();
        }
    };

    const renderPersonSelection = () => {
        if (!sourcePerson) return null;
        
        return (
            <Stack gap="sm">
                <Select
                    label={`Select a person to merge with ${sourcePerson.first} ${sourcePerson.last}:`}
                    placeholder="Choose a person"
                    data={targetPersons
                        .filter(person => person.seed_key !== sourcePerson.seed_key)
                        .map(person => ({
                            value: person.seed_key,
                            label: `${person.first} ${person.last}`
                        }))}
                    onChange={handleTargetPersonChange}
                    searchable
                    clearable
                    required
                />
                
                {selectedTargetPerson && (
                    <>
                    <Divider my="sm" />
                        {(hasNameConflict || hasMiddleNameConflict || hasLastNameConflict || hasDobConflict) && (
                            <>
                            <Alert 
                                variant="light" 
                                color="yellow" 
                                radius="md" 
                                title="Resolve Conflicts" 
                                icon={<IconAlertTriangle size={16} />}
                                // mb="xs"
                            >
                                The selected people have conflicts in key information. Please choose which values to keep.
                            </Alert>
                            <Paper withBorder radius="md" p="md">
                            <Stack gap="xl">
                                    {hasNameConflict && (
                                        <Box>
                                            <Text size="sm" fw={700} mb="xs">First Name:</Text>
                                            <Radio.Group
                                                value={mergeOptions.firstName}
                                                onChange={(value) => handleMergeOptionChange('firstName', value)}
                                            >
                                                <Stack gap={'xs'}>
                                                    <Radio 
                                                        value={sourcePerson.first} 
                                                        label={sourcePerson.first} 
                                                        // icon={({ checked }) => checked ? <Box style={{ width: 14, height: 14, backgroundColor: 'currentColor' }} /> : null}
                                                    />
                                                    <Radio 
                                                        value={selectedTargetPerson.first} 
                                                        label={selectedTargetPerson.first} 
                                                        // icon={({ checked }) => checked ? <Box style={{ width: 14, height: 14, backgroundColor: 'currentColor' }} /> : null}
                                                    />
                                                </Stack>
                                            </Radio.Group>
                                        </Box>
                                    )}

                                    {hasMiddleNameConflict && (
                                        <Box>
                                            <Text size="sm" fw={700} mb="xs">Middle Name:</Text>
                                            <Radio.Group
                                                value={mergeOptions.middleName || ''}
                                                onChange={(value) => handleMergeOptionChange('middleName', value || undefined)}
                                            >
                                                <Stack gap={'xs'}>
                                                    <Radio 
                                                        value={sourcePerson.middle || ''} 
                                                        label={sourcePerson.middle || 'None'} 
                                                        // icon={({ checked }) => checked ? <Box style={{ width: 14, height: 14, backgroundColor: 'currentColor' }} /> : null}
                                                    />
                                                    <Radio 
                                                        value={selectedTargetPerson.middle || ''} 
                                                        label={selectedTargetPerson.middle || 'None'} 
                                                        // icon={({ checked }) => checked ? <Box style={{ width: 14, height: 14, backgroundColor: 'currentColor' }} /> : null}
                                                    />
                                                </Stack>
                                            </Radio.Group>
                                        </Box>
                                    )}

                                    {hasLastNameConflict && (
                                        <Box>
                                            <Text size="sm" fw={700} mb="xs">Last Name:</Text>
                                            <Radio.Group
                                                value={mergeOptions.lastName}
                                                onChange={(value) => handleMergeOptionChange('lastName', value)}
                                            >
                                                <Stack gap={'xs'}>
                                                    <Radio 
                                                        value={sourcePerson.last} 
                                                        label={sourcePerson.last} 
                                                        // icon={({ checked }) => checked ? <Box style={{ width: 14, height: 14, backgroundColor: 'currentColor' }} /> : null}
                                                    />
                                                    <Radio 
                                                        value={selectedTargetPerson.last} 
                                                        label={selectedTargetPerson.last} 
                                                        // icon={({ checked }) => checked ? <Box style={{ width: 14, height: 14, backgroundColor: 'currentColor' }} /> : null}
                                                    />
                                                </Stack>
                                            </Radio.Group>
                                        </Box>
                                    )}

                                    {hasDobConflict && (
                                        <Box>
                                            <Text size="sm" fw={700} mb="xs">Date of Birth:</Text>
                                            <Radio.Group
                                                value={mergeOptions.dob ? mergeOptions.dob.toISOString() : ''}
                                                onChange={(value: string) => handleMergeOptionChange('dob', value ? new Date(value) : undefined)}
                                            >
                                                <Stack gap={'xs'}>
                                                    <Radio 
                                                        value={sourcePerson.dob ? sourcePerson.dob.toISOString() : ''} 
                                                        label={sourcePerson.dob ? sourcePerson.dob.toLocaleDateString() : 'None'} 
                                                        // icon={({ checked }) => checked ? <Box style={{ width: 14, height: 14, backgroundColor: 'currentColor' }} /> : null}
                                                    />
                                                    <Radio 
                                                        value={selectedTargetPerson.dob ? selectedTargetPerson.dob.toISOString() : ''} 
                                                        label={selectedTargetPerson.dob ? selectedTargetPerson.dob.toLocaleDateString() : 'None'} 
                                                        // icon={({ checked }) => checked ? <Box style={{ width: 14, height: 14, backgroundColor: 'currentColor' }} /> : null}
                                                    />
                                                </Stack>
                                            </Radio.Group>
                                        </Box>
                                    )}
                                </Stack>
                                </Paper>
                                    </>
                        )}
                        
                        <Group justify="flex-end" mt="md">
                            <Button variant="outline" onClick={onClose}>Cancel</Button>
                            <Button 
                                onClick={handleContinue}
                                disabled={!selectedTargetPerson}
                            >
                                Continue Merge
                            </Button>
                        </Group>
                    </>
                )}
            </Stack>
        );
    };

    const renderMergePreview = () => {
        if (!selectedTargetPerson || !sourcePerson) return null;

        // Create a map to deduplicate people options
        const uniquePeopleMap = new Map();
        
        // Add all target persons to the map
        targetPersons.forEach(person => {
            uniquePeopleMap.set(person.seed_key, {
                value: person.seed_key,
                label: `${person.first} ${person.last}`
            });
        });
        
        // Add source person to the map
        uniquePeopleMap.set(sourcePerson.seed_key, {
            value: sourcePerson.seed_key,
            label: `${sourcePerson.first} ${sourcePerson.last}`
        });
        
        // Convert the map values to an array
        const peopleOptions = Array.from(uniquePeopleMap.values());

        return (
            <Stack>
                <Text>
                    Below is a preview of what the information for <strong>{mergeOptions.firstName} {mergeOptions.lastName}</strong> will look like after the merge. Please verify/update the information before confirming the merge.
                </Text>
                
                <Paper p="md" withBorder>
                    <Title order={5} mb="md">Personal Information</Title>
                    <PersonalInfoEditor
                        firstName={mergeOptions.firstName}
                        middleName={mergeOptions.middleName}
                        lastName={mergeOptions.lastName}
                        dob={mergeOptions.dob}
                        profileUrl={mergeOptions.profileImage}
                        onUpdateField={handleMergeOptionChange}
                    />
                </Paper>
                
                <Paper p="md" withBorder>
                    <Title order={5} mb="md">Relationships</Title>
                    <Stack>
                        {combinedRelationships.filter(r => r.selected).length > 0 ? (
                            <RelationshipList
                                relationships={combinedRelationships.filter(r => r.selected)}
                                onUpdateRelationship={handleUpdateRelationship}
                                onRemoveRelationship={handleRemoveRelationship}
                                peopleOptions={peopleOptions}
                            />
                        ) : (
                            <Text color="dimmed">No relationships to merge</Text>
                        )}
                        <Button
                            onClick={handleAddRelationship}
                            leftSection={<IconPlus size={16} />}
                            variant="outline"
                        >
                            Add Relationship
                        </Button>
                    </Stack>
                </Paper>
                
                <Paper p="md" withBorder>
                    <Title order={5} mb="md">Life Events</Title>
                    <Stack>
                        {combinedLifeEvents.filter(e => e.selected).length > 0 ? (
                            <LifeEventList
                                lifeEvents={combinedLifeEvents.filter(e => e.selected)}
                                onUpdateLifeEvent={handleUpdateLifeEvent}
                                onRemoveLifeEvent={handleRemoveLifeEvent}
                            />
                        ) : (
                            <Text color="dimmed">No life events to merge</Text>
                        )}
                        <Button
                            onClick={handleAddLifeEvent}
                            leftSection={<IconPlus size={16} />}
                            variant="outline"
                        >
                            Add Life Event
                        </Button>
                    </Stack>
                </Paper>
                
                <Paper p="md" withBorder mt="md">
                    <Title order={5} mb="md">Notes</Title>
                    <NotesEditor
                        notes={mergeOptions.notes}
                        onUpdateNotes={(notes) => handleMergeOptionChange('notes', notes)}
                    />
                </Paper>
                
                <Group justify="flex-end" mt="md">
                    <Button variant="outline" onClick={() => setActiveStep(0)}>Back</Button>
                    <Button 
                        onClick={handleMerge}
                        color="blue"
                    >
                        Confirm Merge
                    </Button>
                </Group>
            </Stack>
        );
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={activeStep === 0 ? "Merge People" : "Preview & Confirm Merge"}
            size="xl"
            // Disable focus trapping to prevent aria-hidden warnings
            trapFocus={false}
            closeOnEscape={true}
        >
            {sourcePerson && (
                <>
                    {activeStep === 0 && renderPersonSelection()}
                    {activeStep === 1 && selectedTargetPerson && renderMergePreview()}
                </>
            )}
        </Modal>
    );
}
